import React from "react";
import { CircularProgress, Divider, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import Chip from "@hyro-components/Chip";
import CollapsibleSection from "@hyro-components/CollapsibleSection";
import Tooltip from "@hyro-components/Tooltip";
import palette from "src/theme/palette";
import useSubscriptionTab from "./SubscriptionTab.hook";
import AccountTeamSection from "./AccountTeamSection";
import GoLiveBanner from "./GoLiveBanner";
import ProductsTable from "./ProductsTable";
import QuotaUsageSummary from "./QuotaUsageSummary";
import SubscriptionDetails from "./SubscriptionDetails";
import SubscriptionEmptyState from "./SubscriptionEmptyState";
import SubscriptionErrorState from "./SubscriptionErrorState";
import {
  ACTIVE_CHIP_STYLE,
  QUOTA_TERM_TOOLTIP,
  RENEWAL_WARNING_CHIP_STYLE
} from "./SubscriptionTab.consts";

const formatTermDate = (value) => (value ? dayjs(value).format("MMM D, YYYY") : null);

const HeaderDivider = () => (
  <Divider
    orientation="vertical"
    flexItem
    sx={{ borderColor: palette.system[300], my: "4px" }}
  />
);

const HeaderField = ({ label, value }) => (
  <Stack direction="row" alignItems="center" gap="6px">
    <Typography variant="body2" sx={{ color: palette.text[700], letterSpacing: "0.15px" }}>
      {label}
    </Typography>
    <Typography variant="subtitle2" sx={{ color: palette.text[800], letterSpacing: "0.1px" }}>
      {value}
    </Typography>
  </Stack>
);

const buildQuotaHeaderSubtitle = (currentTerm) => {
  const start = formatTermDate(currentTerm.termStartDate);
  const end = formatTermDate(currentTerm.termEndDate);
  if (!start || !end) return null;

  return (
    <Stack direction="row" alignItems="center" gap={2} data-testid="quota-header-meta">
      <HeaderDivider />
      <Stack direction="row" alignItems="center" gap="6px">
        <Typography variant="body2" sx={{ color: palette.text[700], letterSpacing: "0.15px" }}>
          Term
        </Typography>
        <Tooltip title={QUOTA_TERM_TOOLTIP} placement="top" variant="light">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: palette.text[700] }} />
        </Tooltip>
      </Stack>
      <HeaderDivider />
      <HeaderField label="Start date" value={start} />
      <HeaderDivider />
      <HeaderField label="End date" value={end} />
    </Stack>
  );
};

const buildSubscriptionDetailsChips = ({ daysToRenewal, isActive, showRenewalWarning }) => {
  const chips = [];

  if (showRenewalWarning) {
    chips.push(
      <Chip
        key="renewal"
        label={`${daysToRenewal} days to renewal`}
        {...RENEWAL_WARNING_CHIP_STYLE}
      />
    );
  }

  if (isActive) {
    chips.push(<Chip key="active" label="Active" {...ACTIVE_CHIP_STYLE} />);
  }

  return chips;
};

const SubscriptionTab = ({ accountId, isAccountContext, salesforceAccountId }) => {
  const {
    currentTerm,
    teamMembers,
    interactionLimit,
    daysToRenewal,
    isActive,
    isNotStarted,
    showRenewalWarning,
    showGoLiveWarning,
    isLoading,
    isError,
    isEmpty
  } = useSubscriptionTab({
    accountId,
    isAccountContext,
    hasSalesforceId: !!salesforceAccountId
  });

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 400 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (isError) {
    return <SubscriptionErrorState />;
  }

  if (isEmpty) {
    return <SubscriptionEmptyState />;
  }

  const detailsChips = buildSubscriptionDetailsChips({ daysToRenewal, isActive, showRenewalWarning });

  return (
    <Stack gap={3}>
      {showGoLiveWarning && <GoLiveBanner salesforceAccountId={salesforceAccountId} />}
      <AccountTeamSection teamMembers={teamMembers} />
      <CollapsibleSection title="Subscription details" headerRight={detailsChips}>
        <SubscriptionDetails term={currentTerm} salesforceAccountId={salesforceAccountId} />
      </CollapsibleSection>
      <CollapsibleSection title={`Products and packages (${currentTerm.products.length})`}>
        <ProductsTable products={currentTerm.products} />
      </CollapsibleSection>
      <CollapsibleSection
        title="Current Quota usage"
        subtitle={buildQuotaHeaderSubtitle(currentTerm)}
      >
        <QuotaUsageSummary
          term={currentTerm}
          interactionLimit={interactionLimit}
          isNotStarted={isNotStarted}
        />
      </CollapsibleSection>
    </Stack>
  );
};

export default SubscriptionTab;
